import { useEffect, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../../../../context/AuthContext/AuthContext';
import { useTranslation } from 'react-i18next';

/**
 * Whether a redirect target stays on this site: a path starting with a single slash. "//host"
 * and any backslash ("/\host", which browsers read as "//host") would lead elsewhere.
 *
 * @param target value of the `redirect` query parameter
 */
const isInternalPath = (target: string): boolean =>
  target.startsWith('/') && !target.startsWith('//') && !target.includes('\\');

export const useLogin = (onTwoFactorRequired?: (email: string, rememberMe: boolean) => void) => {
  const { t } = useTranslation('login');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, user, profile } = useAuth();

  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');

  /**
   * Effect: Redirect user upon successful authentication
   */
  useEffect(() => {
    if (user) {
      // Internal redirect requested by another page (e.g. /apply → login → back to /apply).
      const redirect = searchParams.get('redirect');
      if (redirect && isInternalPath(redirect)) {
        navigate(redirect, { replace: true });
        return;
      }
      if (user.role === 'ROLE_ADMIN') {
        navigate('/admin/dashboard');
      } else if (profile) {
        if (profile.hasActiveInstantFunded) {
          navigate('/');
        } else if (profile.hasActiveFundedChallenge) {
          navigate('/trader-area');
        } else if (profile.hasActiveChallenge) {
          navigate('/client-area');
        } else {
          navigate('/buy-funded');
        }
      }
    }
  }, [user, profile, navigate, searchParams]);

  /**
   * Handles the primary login submission
   */
  const handleLogin = async () => {
    setEmailError(null);
    setPasswordError(null);
    setFormError(null);

    // Basic validation
    if (!email) setEmailError(t('ERROR_EMAIL_ADDRESS_IS_REQUIRED'));
    if (!password) setPasswordError(t('ERROR_PASSWORD_IS_REQUIRED'));
    if (!email || !password) return;

    setIsSubmitting(true);

    try {
      await login(email, password, rememberMe);
    } catch (error: any) {
      /**
       * Handle 2FA Requirement
       * Triggered when the AuthContext detects a 2FA requirement in a 200 OK response
       */
      if (error.message === 'TWO_FACTOR_REQUIRED') {
        onTwoFactorRequired?.(email, rememberMe);
      }
      /**
       * Handle Rate Limiting (429)
       */
      else if (error.status === 429) {
        setSnackbarMessage(t('ERROR_TOO_MANY_LOGIN_ATTEMPTS_PLEASE_TRY_AGAIN_LATER'));
      }
      /**
       * Handle Bad Request (400) — differentiate sub-cases by API message
       */
      else if (error.status === 400) {
        const msg = (error.message || '').toLowerCase();
        if (msg.includes('not registered')) {
          setFormError(t('ERROR_ACCOUNT_NOT_REGISTERED'));
        } else if (msg.includes('unsupported')) {
          setSnackbarMessage(t('ERROR_UNSUPPORTED_LOGIN_TYPE'));
        } else {
          setFormError(t('ERROR_INVALID_CREDENTIALS'));
        }
      }
      /**
       * Handle Invalid Credentials (401)
       */
      else if (error.status === 401) {
        setFormError(t('ERROR_INVALID_CREDENTIALS'));
      }
      /**
       * Generic Error fallback
       */
      else {
        setSnackbarMessage(t('ERROR_AN_ERROR_OCCURED_PLEASE_TRY_AGAIN'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    email, setEmail,
    password, setPassword,
    rememberMe, setRememberMe,
    emailError, passwordError,
    formError,
    isSubmitting, snackbarMessage,
    clearSnackbarMessage: () => setSnackbarMessage(''),
    handleLogin
  };
};